import React from "react";

import { Collapsible } from "~/components/collapsible";
import { CollapsibleContent } from "~/components/collapsible/content";
import { CollapsibleTrigger } from "~/components/collapsible/trigger";
import { IconChevronDown } from "~/components/icon";
import { cv } from "~/tools/class-variance";

import type { ToolCallPartType } from "./_part-types";
import { ToolCallDetail } from "./_tool-call-detail";
import { ChatBubble } from "./chat-bubble";

const segmentVariants = cv("h-1.5 flex-1 rounded-full transition-colors duration-300", {
	defaultVariants: {
		status: "progress",
	},
	variants: {
		status: {
			progress: "bg-grey-300",
			complete: "bg-green-500",
			failed: "bg-red-500",
		},
	},
});

export const ToolCallGroup: React.FC<{ parts: ReadonlyArray<ToolCallPartType> }> = ({ parts }) => {
	const completeCount = parts.filter(
		({ tool_status }) => tool_status === "complete" || tool_status === "failed",
	).length;
	const getToolCallKey = (part: ToolCallPartType, index: number): string =>
		part.part_id ?? `tool:${part.tool_use_id ?? part.tool_name ?? "unknown"}:${index}`;

	return (
		<Collapsible>
			<ChatBubble variant="system-muted">
				<div className="flex flex-col gap-2">
					<CollapsibleTrigger className="flex items-center gap-2 text-left text-grey-700 text-xs hover:text-grey-900">
						<IconChevronDown className="size-3 text-grey-500 transition-transform group-state-closed/collapsible:-rotate-90" />
						<span className="font-medium">Tool calls ({parts.length})</span>
						<span className="ml-auto text-grey-500">
							{completeCount} / {parts.length} complete
						</span>
					</CollapsibleTrigger>
					<div className="flex gap-0.5">
						{parts.map((part, index) => (
							<div
								className={segmentVariants({
									className: undefined,
									status:
										part.tool_status === "complete" || part.tool_status === "failed" ? part.tool_status : undefined,
								})}
								key={getToolCallKey(part, index)}
							/>
						))}
					</div>
					<CollapsibleContent>
						<div className="flex flex-col gap-2 pt-1">
							{parts.map((part, index) => (
								<ToolCallDetail key={getToolCallKey(part, index)} part={part} />
							))}
						</div>
					</CollapsibleContent>
				</div>
			</ChatBubble>
		</Collapsible>
	);
};
